import LanguageSelector from './components/language-selector'
import CopySelector from './components/copy-selector'
import { Base, toast } from '@itsrever/design-system'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { PortalPreview } from '@/ui/components/portal-preview'
import { useCopies, usePortalTranslate } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'
import { updateCopies } from '@/redux/api/settings-api'
import { SettingsApiUpdateCopiesRequest } from '@itsrever/rever-api'
import PageIcon from '@/assets/images/icons/PageIcon.svg'
import LanguageIcon from '@/assets/images/icons/LanguageIcon.svg'
import TextBlockIcon from '@/assets/images/icons/TextBlockIcon.svg'
import MobileIcon from '@/assets/images/icons/MobileIcon.svg'
import TextBoldIcon from '@/assets/images/icons/TextBoldIcon.svg'
import { useDialog } from '@/ui/components/dialog/dialog-provider'
import { useTranslation } from 'react-i18next'
import posthog from 'posthog-js'
import {
  languages,
  useEditableCopies
} from '@/ui/components/custom-copies/utils/utils'
import { Roles } from '@/utils'
import { SaveBar } from '@/ui/shared'

export const CustomCopies = () => {
  const { t } = useTranslation()

  const ecommerceId = useAppSelector(
    (state) => state.generalData.selectedEcommerce
  )
  const { ecommerceCopies, defaultCopies, reloadCopies } = useCopies()

  const pageComponent = useRef<HTMLInputElement>(null)
  const [selectedPageIndex, setSelectedPageIndex] = useState(0)
  const [selectedLang, setSelectedLang] = useState('EN')
  sessionStorage.setItem('UILang', selectedLang)

  const editableCopiesData = useEditableCopies()
  const defaultSelectedCopy = () => {
    if (editableCopiesData[selectedPageIndex].copies.length !== 0) {
      return editableCopiesData[selectedPageIndex].copies[0]?.key ?? ''
    } else {
      return ''
    }
  }
  const [selectedCopy, setSelectedCopy] = useState(defaultSelectedCopy())
  const [selectCopiesList, setSelectCopiesList] = useState(
    editableCopiesData[selectedPageIndex].copies
  )

  interface EditingValue {
    lang: string
    editedText: string
    edited: boolean
  }

  const [copyEditingValues, setCopyEditingValues] = useState<EditingValue[]>([])
  const [defaultCopyValues, setDefaultCopyValues] = useState<string[]>([])
  const [editedCopies, setEditedCopies] = useState<Record<string, string>>({})

  usePortalTranslate(editedCopies)

  const [anyCopyEdited, setAnyCopyEdited] = useState(false)
  const [saving, setSaving] = useState(false)
  const discardEditedCopies = () => {
    setCopyEditingValues(storedEditingValues(ecommerceCopies, selectedCopy))
    setEditedCopies({})
    setAnyCopyEdited(false)
  }
  const setNewCopyValue = (lang: string, value: string) => {
    const newCopy: { [key: string]: string } = {
      [lang + '-' + selectedCopy]: value
    }
    const newEditedCopies = { ...editedCopies, ...newCopy }
    setEditedCopies(newEditedCopies)
  }
  const onSaveEditedCopies = () => {
    const editedLangs = copyEditingValues.flatMap((copy) => {
      if (copy.edited) {
        return { lang_tag: copy.lang, text: copy.editedText }
      } else {
        return []
      }
    })
    const copyDetails = {
      [selectedCopy]: {
        copies: editedLangs
      }
    }
    const copiesData: SettingsApiUpdateCopiesRequest = {
      pbUpdateEcommerceCopyRequest: {
        copy_details: copyDetails,
        ecommerce_id: ecommerceId
      }
    }
    posthog?.capture('Copy edited', {
      ecommerceId: ecommerceId,
      editedCopyId: selectedCopy,
      editedLanguages: editedLangs.map((lang) => lang.lang_tag.toUpperCase())
    })
    setSaving(true)
    updateCopies(copiesData)
      .then(() => {
        toast({
          text: t('custom_copies.changes_saved'),
          variant: 'success'
        })
        reloadCopies()
        setAnyCopyEdited(false)
      })
      .catch(() => {
        toast({
          text: t('custom_copies.changes_not_saved'),
          variant: 'error'
        })
      })
      .finally(() => setSaving(false))
  }
  const onCopyEdited = (index: number, text: string, lang: string) => {
    setCopyEditingValues([
      ...copyEditingValues.slice(0, index),
      {
        editedText: text,
        edited: true,
        lang: lang
      },
      ...copyEditingValues.slice(index + 1)
    ])
    setAnyCopyEdited(true)
  }
  const onKeyPressed = (
    lang: string,
    e: KeyboardEvent<HTMLTextAreaElement>
  ): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.currentTarget.blur()
      setNewCopyValue(lang, e.currentTarget.value)
    }
  }

  const showDialog = useDialog()

  const onPageSelected = async (index: number) => {
    if (anyCopyEdited) {
      const confirmed = await showDialog({
        title: t('custom_copies.dialog_unsaved_title'),
        message: t('custom_copies.dialog_unsaved_description1')
      })
      if (confirmed) {
        setAnyCopyEdited(false)
        setSelectedPageIndex(index)
      }
    } else {
      setSelectedPageIndex(index)
    }
  }
  const onCopySelected = async (copyCode: string) => {
    if (anyCopyEdited) {
      const confirmed = await showDialog({
        title: t('custom_copies.dialog_unsaved_title'),
        message: t('custom_copies.dialog_unsaved_description2')
      })
      if (confirmed) {
        setAnyCopyEdited(false)
        setSelectedCopy(copyCode)
      }
    } else {
      setSelectedCopy(copyCode)
    }
  }
  useEffect(() => {
    setSelectCopiesList(editableCopiesData[selectedPageIndex].copies)
    setSelectedCopy(defaultSelectedCopy())
    setEditedCopies({})
  }, [selectedPageIndex])

  useEffect(() => {
    const defaultCopyValues = languages.map((lang) => {
      return defaultCopies[lang.code + '-' + selectedCopy]
    })
    setEditedCopies({})
    setCopyEditingValues(storedEditingValues(ecommerceCopies, selectedCopy))
    setDefaultCopyValues(defaultCopyValues)
  }, [ecommerceCopies, defaultCopies, selectedCopy])

  const role = useAppSelector(
    (store) => store.userApi.getMe.response.user?.role
  )
  const disabledSave = role !== Roles.ReadWrite
  const editedCount = copyEditingValues.filter((copy) => copy.edited).length

  return (
    <>
      <div className="w-full max-w-[1000px]" ref={pageComponent}>
        <div className="my-3 flex min-w-[800px] items-end gap-5 p-3">
          <div className="flex-none">
            <p className="mb-1 flex text-sm text-gray-500">
              <img className="mr-1 size-5 opacity-50" src={PageIcon} />
              {t('custom_copies.pages_label')}
            </p>
            <div className="flex">
              {editableCopiesData.map((page, index) => {
                return (
                  <p
                    key={index}
                    onClick={() => onPageSelected(index)}
                    className={
                      'cursor-pointer text-sm first:rounded-l-md last:rounded-r-md px-3 py-1 transition-all border-y border-l last:border-r border-solid border-gray-400 hover:bg-gray-300 ' +
                      (selectedPageIndex === index
                        ? 'bg-gray-300'
                        : 'bg-gray-50')
                    }
                  >
                    {t(page.text) ?? ''}
                  </p>
                )
              })}
            </div>
          </div>
          <div className="flex-none">
            <p className="mb-1 flex text-sm text-gray-500">
              <img className="mr-1 size-5 opacity-50" src={LanguageIcon} />
              {t('custom_copies.view_language_label')}
            </p>
            <LanguageSelector
              selectedLang={selectedLang}
              onChange={(lang) => {
                setSelectedLang(lang)
                sessionStorage.setItem('UILang', lang)
              }}
            />
          </div>
          <div className="min-w-[50px] flex-1 flex-col">
            <p className="mb-1 flex text-sm text-gray-500">
              <img className="mr-1 size-5 opacity-50" src={TextBlockIcon} />
              {t('custom_copies.copy_label')}
            </p>
            <CopySelector
              copies={selectCopiesList}
              selectedCopy={selectedCopy}
              onChange={(copyCode) => onCopySelected(copyCode)}
            />
          </div>
        </div>
        <div className="flex gap-5">
          <div className="mt-5">
            <PortalPreview
              portalView={editableCopiesData[selectedPageIndex].key}
              copies={editedCopies}
            />
          </div>
          <div className="w-full p-2">
            <div className="grid grid-cols-[100px_1fr_1fr] gap-x-4 gap-y-6">
              <p></p>
              <p className="px-3 text-sm text-gray-400">
                {t('custom_copies.your_copy_label')}
              </p>
              <p className="text-sm text-gray-400">
                {t('custom_copies.default_copy_label')} REVER
              </p>
            </div>
            <div className="mt-2">
              {languages.map((lang, index) => {
                return (
                  <Base.Card
                    key={lang.code}
                    className="mb-3 grid h-auto grid-cols-[100px_1fr_1fr] gap-x-4 gap-y-6 p-2"
                  >
                    <p className="flex items-center justify-center p-3">
                      {t(lang.name)}
                    </p>
                    <div className="relative flex w-full">
                      <textarea
                        className={
                          'w-full rounded-md border p-3 text-sm transition-all ease-in-out outline-3 outline-gray-200 hover:outline  ' +
                          (copyEditingValues[index] &&
                          copyEditingValues[index].edited
                            ? ' border-blue-400 bg-blue-50'
                            : ' border-gray-300 bg-white')
                        }
                        rows={2}
                        value={copyEditingValues[index]?.editedText ?? ''}
                        onKeyDown={(e) => {
                          onKeyPressed(lang.code, e)
                        }}
                        onBlur={(e) =>
                          setNewCopyValue(lang.code, e.target.value)
                        }
                        onChange={(e) =>
                          onCopyEdited(index, e.target.value, lang.code)
                        }
                      />
                    </div>
                    <p className="p-2 text-sm text-gray-500">
                      {defaultCopyValues[index]}
                    </p>
                  </Base.Card>
                )
              })}
            </div>
            <div className="mt-5 flex items-center align-middle text-sm text-gray-500">
              <span className="mr-2 size-7 rounded-full bg-slate-300 p-1">
                <img className="mr-1 size-5 opacity-50" src={TextBoldIcon} />
              </span>
              <span>
                <span
                  dangerouslySetInnerHTML={{
                    __html:
                      t('custom_copies.render_tags_warning1') +
                      ' &lt;/br&gt; ' +
                      t('custom_copies.render_tags_warning2') +
                      ' &lt;b&gt;&lt;/b&gt;'
                  }}
                />
              </span>
            </div>
            <div className="mt-5 flex items-center align-middle text-sm text-gray-500">
              <span className="mr-2 size-7 rounded-full bg-slate-300 p-1">
                <img className="mr-1 size-5 opacity-50" src={MobileIcon} />
              </span>
              <span>{t('custom_copies.mobile_preview_warning')}</span>
            </div>
          </div>
        </div>
        <SaveBar
          editable={!disabledSave}
          changes={editedCount}
          saving={saving}
          onDiscard={discardEditedCopies}
          onSave={onSaveEditedCopies}
        />
      </div>
    </>
  )
}

function storedEditingValues(
  ecommerceCopies: Record<string, string>,
  selectedCopy: string
) {
  return languages.map((lang) => ({
    lang: lang.code,
    editedText: ecommerceCopies[lang.code + '-' + selectedCopy],
    edited: false
  }))
}

export default CustomCopies
